import { useId, type ComponentPropsWithRef } from 'react'
import { useTranslation } from 'react-i18next'
import { cx } from '../lib/cx'
import { Icon } from './Icon'
import './Field.css'

export interface TextFieldProps extends Omit<ComponentPropsWithRef<'input'>, 'prefix'> {
  label: string
  /** Hide the label visually (it is still read out), e.g. inside a table row with column headings. */
  hideLabel?: boolean
  hint?: string
  /** Shown under the field with a 2 dp danger border. Not a live region: the form announces errors. */
  error?: string
  /** Text before the value, such as "K". */
  prefix?: string
  /** Adds "(optional)" after the label. */
  optional?: boolean
  /** Styles the value as an amount: tabular figures, heading size. */
  amount?: boolean
  fieldClassName?: string
}

/** A labelled text input, 56 dp tall, with an optional hint and an error under the field. */
export function TextField({
  label,
  hideLabel = false,
  hint,
  error,
  prefix,
  optional = false,
  amount = false,
  fieldClassName,
  className,
  id,
  disabled,
  ...rest
}: TextFieldProps) {
  const { t } = useTranslation()
  const generatedId = useId()
  const inputId = id ?? generatedId
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`
  const describedBy = [rest['aria-describedby'], hint ? hintId : undefined, error ? errorId : undefined]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={cx('field', error && 'field--error', disabled && 'field--disabled', fieldClassName)}>
      <label htmlFor={inputId} className={cx('field__label', hideLabel && 'visually-hidden')}>
        {label}
        {optional && <span className="field__optional"> ({t('common.optional')})</span>}
      </label>
      {hint && (
        <p id={hintId} className={cx('field__hint', hideLabel && 'visually-hidden')}>
          {hint}
        </p>
      )}
      <div className="field__control">
        {prefix && (
          <span className="field__prefix" aria-hidden="true">
            {prefix}
          </span>
        )}
        <input
          {...rest}
          id={inputId}
          disabled={disabled}
          className={cx('field__input', amount && 'field__input--amount', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
        />
      </div>
      {error && (
        <p id={errorId} className="field__error">
          <Icon name="error" />
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}
